import { RadioChangeEvent, Space } from 'antd';
import { pipe } from 'ramda';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  SubmissionHoursConfiguration,
  SubmissionHoursConstrainType,
} from 'api/timesheets/configuration/submission-hours';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { Hours } from 'components/organisms/user-capacity';
import { isEmpty, Just, map, Nothing, ofNullable, orElse } from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useTranslation } from 'hooks/use-translation';
import { defaultCapacity } from '../capacity';
import { ControlPanel } from '../control-panel';
import { SetState, State } from '../state';
import { ConstrainItem } from './constrain-item';

const StyledRadioGroup = styled(RadioGroup)`
  ${Radio} {
    color: var(--gray-80);
    display: inline-flex;
    align-items: center;
    min-height: 40px;
  }
`;

interface Props {
  submissionHours: State<SubmissionHoursConfiguration>;
  onSubmissionHoursChange: SetState<SubmissionHoursConfiguration>;
  capacity: State<Hours>;
}
const defaultSubmissionHoursConfiguration = {
  constraintType: SubmissionHoursConstrainType.IsEmpty,
} as SubmissionHoursConfiguration;

export const SubmissionHours: FC<Props> = ({
  submissionHours,
  capacity,
  onSubmissionHoursChange,
}) => {
  const label = useTranslation(
    'settings.timesheets.controls.submission_hours.label',
  );
  const description = useTranslation(
    'settings.timesheets.controls.submission_hours.description',
  );
  const submissionHoursConfig = useMemo(
    () =>
      pipe(
        () => submissionHours,
        maybeValueOf,
        orElse(defaultSubmissionHoursConfiguration),
      )(),
    [submissionHours],
  );
  const disabled = useMemo(
    () => pipe(() => submissionHours, maybeValueOf, isEmpty)(),
    [submissionHours],
  );
  const _capacity = useMemo(
    () => pipe(() => capacity, maybeValueOf, orElse(defaultCapacity))(),
    [capacity],
  );
  const _submissionHours = useMemo(
    () =>
      pipe(
        () =>
          submissionHoursConfig.constraintType !==
          SubmissionHoursConstrainType.IsEmpty
            ? Just(submissionHoursConfig.value)
            : Nothing(),
        orElse(_capacity),
      )(),
    [submissionHoursConfig, _capacity],
  );
  const onTypeChange = useCallback(
    (e: RadioChangeEvent) => {
      onSubmissionHoursChange(
        pipe(
          () => e.target.value,
          ofNullable,
          map((x) =>
            x === SubmissionHoursConstrainType.IsEmpty
              ? {
                  constraintType: x,
                }
              : {
                  constraintType: x,
                  value: _submissionHours,
                },
          ),
          orElse(defaultSubmissionHoursConfiguration),
        )(),
      );
    },
    [_submissionHours, onSubmissionHoursChange],
  );
  const onHoursChange = useCallback(
    (next: Hours) => {
      if (
        submissionHoursConfig.constraintType !==
        SubmissionHoursConstrainType.IsEmpty
      ) {
        onSubmissionHoursChange({
          ...submissionHoursConfig,
          value: next,
        });
      }
    },
    [onSubmissionHoursChange, submissionHoursConfig],
  );

  return (
    <ControlPanel label={label} descriptions={[description]}>
      <StyledRadioGroup
        value={submissionHoursConfig.constraintType}
        onChange={onTypeChange}
        disabled={disabled}
      >
        <Space direction="vertical">
          {[
            SubmissionHoursConstrainType.IsEmpty,
            SubmissionHoursConstrainType.GreaterThan,
            SubmissionHoursConstrainType.LessThan,
            SubmissionHoursConstrainType.Equals,
          ].map((type) => (
            <Radio value={type} key={type}>
              <ConstrainItem
                choosenConstrainType={submissionHoursConfig.constraintType}
                capacity={_capacity}
                hours={_submissionHours}
                onChange={onHoursChange}
                constrainType={type}
              />
            </Radio>
          ))}
        </Space>
      </StyledRadioGroup>
    </ControlPanel>
  );
};
